import { useEffect } from "react"

import { Flex, Form, Modal, Text } from "@/atoms"
import { getIssueKeyFromInput } from "@/domain/ticketing"
import { getUnavailableVendorMessage } from "@/helpers/error.helper"
import { buildLinkTicketRequest } from "@/helpers/ticketing.helpers"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { useLinkTicketToIssue } from "@/organisms/Drawers/applications/triage/useTriage"
import { FieldLinkTicket } from "./FieldLinkTicket"

import styles from "./CreateTicketButton.module.scss"

import type { LinkTicketModalProps } from "./CreateTicketButton.types"

export const LinkTicketModal = (props: LinkTicketModalProps) => {
  const { open, system, issueId, onCancel } = props
  const [form] = Form.useForm()
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()
  const id = "linkJiraIssueForm"
  const name = "link"
  const value = Form.useWatch(name, form)
  const { t: tLink } = useTranslation("translation", {
    keyPrefix: system === "jira" ? "jiraIssueLink" : "ticketLink"
  })
  const { t: tSystems } = useTranslation("translation", {
    keyPrefix: "ticketing.systems"
  })
  const vendor = { vendor: tSystems(system) }
  const fieldIsTouched = form.isFieldTouched(name)
  const { link, isLinking, message } = useLinkTicketToIssue()

  useEffect(() => {
    if (!open) form.resetFields()
    if (fieldIsTouched) form.setFields([{ name, errors: [] }])
  }, [open, form, fieldIsTouched])

  return (
    <Modal
      destroyOnHidden
      title={tLink("title", vendor)}
      open={open}
      className={styles.modalLinkJira}
      onCancel={onCancel}
      okText={tLink("action", vendor)}
      okButtonProps={{
        htmlType: "submit",
        form: id,
        loading: isLinking,
        disabled: !value || isLinking
      }}
    >
      <Flex vertical gap={8}>
        <Text>{tLink("description", vendor)}</Text>
        <Form
          autoComplete="off"
          form={form}
          id={id}
          onFinish={async (values) => {
            try {
              const issueKey = getIssueKeyFromInput(system, values[name])

              const response = await link(
                buildLinkTicketRequest(system, issueId, issueKey)
              )

              if (!response.ok) throw Error(t("error.serverGeneralError"))

              message.success(tLink("linkSuccess", vendor))
              onCancel()
            } catch (error: any) {
              let message = error?.data?.detail || t("error.serverGeneralError")

              if (error?.status >= 500)
                message =
                  getUnavailableVendorMessage(error) ??
                  t("error.serverGeneralError")

              form.setFields([{ name, errors: [message], touched: false }])
              logger.error(error, {
                issueId,
                system,
                message,
                custom_event:
                  EVENTS.ERROR_EVENTS.JIRA.LINK_JIRA_TO_ISSUE_FAILURE
              })
            }
          }}
        >
          <FieldLinkTicket
            name={name}
            placeholder={tLink("placeholder", vendor)}
            t={t}
          />
        </Form>
      </Flex>
    </Modal>
  )
}
